import React, { useState } from 'react';

export default function PieChart({ data, size = 80 }) {
  const [hoveredSegment, setHoveredSegment] = useState(null);
  if (!data || Object.keys(data).length === 0) {
    return null;
  }

  const filteredData = Object.entries(data).filter(([_, value]) => {
    if (!value) return false;
    const str = value.toString().trim();
    if (!str) return false;
    const num = parseFloat(str.replace(/[^0-9.]/g, '') || '0');
    return num > 0;
  });
  
  if (filteredData.length === 0) {
    return null;
  }

  // Calculate total
  const total = filteredData.reduce((sum, [_, value]) => {
    const num = parseFloat(value?.toString().replace(/[^0-9.]/g, '') || '0');
    return sum + num;
  }, 0);

  if (total === 0) {
    return null;
  }

  // Generate colors
  const defaultColors = {
    overseas: '#FF6B6B',
    kannada: '#4ECDC4',
    telugu: '#45B7D1',
    hindi: '#FFA07A',
    tamil: '#98D8C8',
    malayalam: '#F7DC6F',
    other: '#BB8FCE'
  };

  const segments = filteredData.map(([key, value]) => {
    const num = parseFloat(value?.toString().replace(/[^0-9.]/g, '') || '0');
    const percentage = (num / total) * 100;
    return {
      key: key.charAt(0).toUpperCase() + key.slice(1),
      percentage,
      value,
      color: defaultColors[key] || '#85C1E2'
    };
  }).filter(s => s.percentage > 0);

  const radius = size / 2;
  const center = radius;
  let currentAngle = -90; // Start from top

  // Create pie slices using paths
  const createSlice = (startAngle, endAngle, color) => {
    const startAngleRad = (startAngle * Math.PI) / 180;
    const endAngleRad = (endAngle * Math.PI) / 180;
    
    const x1 = center + radius * Math.cos(startAngleRad);
    const y1 = center + radius * Math.sin(startAngleRad);
    const x2 = center + radius * Math.cos(endAngleRad);
    const y2 = center + radius * Math.sin(endAngleRad);
    
    const largeArcFlag = endAngle - startAngle > 180 ? 1 : 0;
    
    return `M ${center} ${center} L ${x1} ${y1} A ${radius} ${radius} 0 ${largeArcFlag} 1 ${x2} ${y2} Z`;
  };

  return (
    <div className="pie-chart-container" style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center', gap: '8px', position: 'relative' }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ cursor: 'pointer' }}>
        {segments.map((segment, index) => {
          const startAngle = currentAngle;
          const angle = (segment.percentage / 100) * 360;
          const endAngle = currentAngle + angle;
          currentAngle = endAngle;
          const isHovered = hoveredSegment === index;
          
          return (
            <path
              key={index}
              d={createSlice(startAngle, endAngle, segment.color)}
              fill={segment.color}
              stroke="#FFFFFF"
              strokeWidth={isHovered ? "2" : "1"}
              opacity={isHovered ? 0.9 : 1}
              onMouseEnter={() => setHoveredSegment(index)}
              onMouseLeave={() => setHoveredSegment(null)}
              style={{ cursor: 'pointer', transition: 'opacity 0.2s' }}
            />
          );
        })}
      </svg>
      {hoveredSegment !== null && segments[hoveredSegment] && (
        <div 
          className="pie-chart-tooltip"
          style={{
            position: 'absolute',
            top: '-40px',
            left: '50%',
            transform: 'translateX(-50%)',
            backgroundColor: 'rgba(0, 0, 0, 0.9)',
            color: 'white',
            padding: '6px 12px',
            borderRadius: '6px',
            fontSize: '12px',
            fontWeight: '600',
            whiteSpace: 'nowrap',
            zIndex: 1000,
            pointerEvents: 'none',
            boxShadow: '0 2px 8px rgba(0,0,0,0.3)'
          }}
        >
          {segments[hoveredSegment].key}: {segments[hoveredSegment].value} ({segments[hoveredSegment].percentage.toFixed(1)}%)
        </div>
      )}
      <div className="pie-chart-legend" style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', justifyContent: 'center' }}>
        {segments.map((segment, index) => (
          <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '11px' }}>
            <div style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: segment.color }} />
            <span>{segment.key}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

